import React, { useState } from 'react';
import {
  Typography,
  Button,
  TextField,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import apiClient from '@uniqcamp/shared/services/api.service';
import { useToast } from '../../contexts/ToastContext';
import { useFeatureAccess } from '../../hooks/useFeatureAccess';
import type { SchoolsResponse } from '../../types';

export default function SubjectsTab() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const { canEdit } = useFeatureAccess('staffSubjects');

  const [subjects, setSubjects] = useState<string[]>([]);
  const [addOpen, setAddOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState(-1);
  const [subjectName, setSubjectName] = useState('');

  const { data: schoolsData } = useQuery<SchoolsResponse>('schools', async () => {
    const res = await apiClient.get('/schools');
    return res.data as SchoolsResponse;
  });
  const school = schoolsData?.schools?.[0];

  React.useEffect(() => {
    const list = school?.settings?.subjects ?? [];
    setSubjects(Array.isArray(list) ? [...list] : []);
  }, [school?.settings?.subjects]);

  const saveMutation = useMutation(
    async (payload: { _id: string; settings: Record<string, unknown> }) => {
      const res = await apiClient.put(`/schools/${payload._id}`, payload);
      return res.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('schools');
        showToast('success', 'Saved.');
      },
      onError: (err: unknown) => {
        const e = err as { response?: { data?: { message?: string } } };
        showToast('error', e.response?.data?.message ?? 'Failed to save');
      },
    }
  );

  const handleSave = (newList: string[]) => {
    if (!school) return;
    setSubjects(newList);
    saveMutation.mutate({
      _id: school._id,
      settings: {
        ...school.settings,
        subjects: newList,
      },
    });
  };

  const handleAdd = () => {
    setSubjectName('');
    setAddOpen(true);
  };

  const handleAddSubmit = () => {
    const name = subjectName.trim();
    if (!name) {
      showToast('error', 'Subject name is required');
      return;
    }
    if (subjects.includes(name)) {
      showToast('error', 'Subject already exists');
      return;
    }
    handleSave([...subjects, name]);
    setAddOpen(false);
  };

  const handleEdit = (idx: number) => {
    setEditingIndex(idx);
    setSubjectName(subjects[idx] ?? '');
    setEditOpen(true);
  };

  const handleEditSubmit = () => {
    if (editingIndex < 0 || editingIndex >= subjects.length) return;
    const name = subjectName.trim();
    if (!name) {
      showToast('error', 'Subject name is required');
      return;
    }
    const other = subjects.filter((_, i) => i !== editingIndex);
    if (other.includes(name)) {
      showToast('error', 'Subject already exists');
      return;
    }
    const newList = subjects.map((s, i) => (i === editingIndex ? name : s));
    handleSave(newList);
    setEditOpen(false);
    setEditingIndex(-1);
  };

  const handleDelete = (idx: number) => {
    if (!window.confirm(`Remove subject "${subjects[idx]}"?`)) return;
    handleSave(subjects.filter((_, i) => i !== idx));
  };

  return (
    <Box>
      {canEdit && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleAdd}>
            Add Subject
          </Button>
        </Box>
      )}
      {subjects.length === 0 ? (
        <Paper sx={{ py: 4, px: 2, textAlign: 'center' }}>
          <Typography color="text.secondary">No subjects yet. Add subjects here; then assign them to teachers on the Staff tab.</Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Subject</TableCell>
                {canEdit && <TableCell align="right">Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {subjects.map((name, idx) => (
                <TableRow key={idx}>
                  <TableCell>{name}</TableCell>
                  {canEdit && (
                    <TableCell align="right">
                      <IconButton size="small" title="Edit" onClick={() => handleEdit(idx)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" title="Delete" color="error" onClick={() => handleDelete(idx)} disabled={saveMutation.isLoading}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Add Subject</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Subject name"
            value={subjectName}
            onChange={(e) => setSubjectName(e.target.value)}
            margin="normal"
            placeholder="e.g. Math, Science"
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)}>Cancel</Button>
          {canEdit && (
            <Button variant="contained" onClick={handleAddSubmit} disabled={saveMutation.isLoading}>
              Add
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog open={editOpen} onClose={() => { setEditOpen(false); setEditingIndex(-1); }} maxWidth="xs" fullWidth>
        <DialogTitle>Edit Subject</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Subject name"
            value={subjectName}
            onChange={(e) => setSubjectName(e.target.value)}
            margin="normal"
            placeholder="e.g. Math, Science"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setEditOpen(false); setEditingIndex(-1); }}>Cancel</Button>
          {canEdit && (
            <Button variant="contained" onClick={handleEditSubmit} disabled={saveMutation.isLoading}>
              Save
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}
